import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { JournalEntry } from "./JournalEntry";
import { supabase } from "@/integrations/supabase/client";

interface JournalEntry {
  id: string;
  title: string;
  content: string;
  category: string;
  child_id?: string;
  created_at: string;
  child_profiles?: {
    name: string;
  };
}

export const JournalEntryList = () => {
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["journalEntries"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No authenticated session");

      const { data, error } = await supabase
        .from("journal_entries")
        .select(`
          *,
          child_profiles (
            name
          )
        `)
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  const { data: children = [] } = useQuery({
    queryKey: ["childProfiles"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No authenticated session");

      const { data, error } = await supabase
        .from("child_profiles")
        .select("*")
        .eq('parent_id', session.user.id);
      
      if (error) throw error;
      return data || [];
    },
  });

  const childOptions = children.map(child => ({
    value: child.id,
    label: child.name,
  }));

  if (isLoading) {
    return <div className="text-center">Loading entries...</div>;
  }

  if (!entries.length) {
    return (
      <div className="text-center text-gray-500">
        No journal entries yet. Create your first entry above.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {entries.map((entry: JournalEntry) => (
        <Card key={entry.id}>
          <JournalEntry entry={entry} childOptions={childOptions} />
        </Card>
      ))}
    </div>
  );
};
